// Jun-2026 — Operations Dashboard (Overview section): management view of all
// Operational (ops-enabled) projects. Portfolio KPI cards + searchable /
// client-filterable project table with vertical scroller. Read-only; gated by
// the dedicated `ops_dashboard.view` permission. ₹ masked without
// boq_commercials (percentages remain visible).
import { useEffect, useMemo, useState } from "react";
import { api, formatINR } from "@/lib/api";
import { Search, RefreshCw, ShieldAlert, HardHat, FileText, Send, TrendingUp, Landmark, Users, CalendarClock, CalendarX } from "lucide-react";
import OpsDaywiseFeed from "@/components/OpsDaywiseFeed";
import OpsBlockerScopeCard from "@/components/OpsBlockerScopeCard";

const inr = (v) => (v == null ? "•••" : formatINR(v));

const KPI_STYLES = [
  { border: "border-emerald-200", bg: "bg-emerald-50/70", label: "text-emerald-700", value: "text-emerald-900" },
  { border: "border-sky-200", bg: "bg-sky-50/70", label: "text-sky-700", value: "text-sky-900" },
  { border: "border-orange-200", bg: "bg-orange-50/70", label: "text-orange-700", value: "text-orange-900" },
  { border: "border-violet-200", bg: "bg-violet-50/70", label: "text-violet-700", value: "text-violet-900" },
  { border: "border-teal-200", bg: "bg-teal-50/70", label: "text-teal-700", value: "text-teal-900" },
  { border: "border-amber-200", bg: "bg-amber-50/70", label: "text-amber-700", value: "text-amber-900" },
  { border: "border-rose-200", bg: "bg-rose-50/70", label: "text-rose-700", value: "text-rose-900" },
];

function Kpi({ i, label, value, sub, icon: Icon, testid }) {
  const s = KPI_STYLES[i % KPI_STYLES.length];
  return (
    <div className={`border-2 ${s.border} ${s.bg} rounded-lg px-4 py-3 min-w-[150px] flex-1`} data-testid={testid}>
      <div className={`flex items-center gap-1.5 text-[11px] uppercase font-bold ${s.label}`}>
        {Icon && <Icon size={13} />} {label}
      </div>
      <div className={`${String(value ?? "").length > 9 ? "text-lg" : "text-2xl"} font-black tabular-nums leading-tight break-words ${s.value}`}>{value}</div>
      {sub && <div className={`text-[10px] ${s.label} opacity-80`}>{sub}</div>}
    </div>
  );
}

function ProgressBar({ pct }) {
  if (pct == null) return <span className="text-slate-400 text-[10px]">no baseline</span>;
  const capped = Math.min(pct, 100);
  return (
    <div className="flex items-center gap-2 min-w-[120px]">
      <div className="flex-1 h-2 bg-slate-200 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${pct > 100 ? "bg-orange-500" : pct >= 75 ? "bg-emerald-600" : pct >= 40 ? "bg-sky-600" : "bg-amber-500"}`}
          style={{ width: `${capped}%` }} />
      </div>
      <span className={`text-xs font-black tabular-nums ${pct > 100 ? "text-orange-700" : "text-slate-900"}`}>{pct}%</span>
    </div>
  );
}

function DueBadge({ r }) {
  if (!r.due_status) return <span className="text-slate-400">—</span>;
  const over = r.due_status === "Overdue";
  return (
    <div data-testid={`ops-due-${r.project_code}`}>
      <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold whitespace-nowrap ${over ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800"}`}>
        {over ? `Overdue by ${r.due_days}d` : r.due_days === 0 ? "Due today" : `Within due · ${r.due_days}d left`}
      </span>
      <div className="text-[9px] text-slate-400 font-mono mt-0.5">ends {r.project_end_date}</div>
    </div>
  );
}

export default function OpsDashboardPage() {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const [q, setQ] = useState("");
  const [client, setClient] = useState("");
  const [ptype, setPtype] = useState("");
  const [showCompleted, setShowCompleted] = useState(false);
  const load = () => {
    api.get("/ops-dashboard")
      .then((r) => { setData(r.data); setErr(""); })
      .catch((e) => setErr(e?.response?.data?.detail || "Operations Dashboard is not available"));
  };
  useEffect(load, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sep-2026 (user request) — Completed projects are hidden by default;
  // the "Show completed" toggle reveals them.
  const baseProjects = useMemo(
    () => (data?.projects || []).filter((r) => showCompleted || r.status !== "Completed"),
    [data, showCompleted]);
  const completedCount = useMemo(
    () => (data?.projects || []).filter((r) => r.status === "Completed").length, [data]);

  const rows = useMemo(() => {
    let out = baseProjects;
    if (client) out = out.filter((r) => (client === "__none__" ? !r.client_name : r.client_name === client));
    if (ptype) out = out.filter((r) => (ptype === "__none__" ? !r.work_type : r.work_type === ptype));
    const s = q.trim().toLowerCase();
    if (s) out = out.filter((r) =>
      (r.project_name || "").toLowerCase().includes(s)
      || (r.project_code || "").toLowerCase().includes(s)
      || (r.ro_number || "").toLowerCase().includes(s));
    return out;
  }, [baseProjects, q, client, ptype]);

  const workTypes = useMemo(() => {
    const set = new Set();
    let untyped = false;
    baseProjects.forEach((r) => { if ((r.work_type || "").trim()) set.add(r.work_type.trim()); else untyped = true; });
    return { list: [...set].sort(), untyped };
  }, [baseProjects]);

  const clientCounts = useMemo(() => {
    const m = new Map();
    baseProjects.forEach((r) => {
      const key = r.client_name || "__none__";
      m.set(key, (m.get(key) || 0) + 1);
    });
    return [...m.entries()].sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]));
  }, [baseProjects]);

  if (err) return (
    <div className="flex items-center gap-2 text-amber-800 bg-amber-50 border border-amber-300 rounded-lg px-4 py-3 text-sm" data-testid="ops-dash-forbidden">
      <ShieldAlert size={16} /> {err}
    </div>
  );
  if (!data) return <div className="text-sm text-slate-400 py-16 text-center" data-testid="ops-dash-loading">Loading Operations Dashboard…</div>;

  const k = data.kpis || {};
  const show = (key) => !data.restricted || (data.sections_allowed || []).includes(key);
  return (
    <div className="space-y-5" data-testid="ops-dashboard">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-orange-700 font-bold">Overview</div>
          <h1 className="font-display text-2xl font-black text-slate-900">Operations Dashboard</h1>
          <div className="text-xs text-slate-500">All operational projects at a glance · as of {data.as_of_date}</div>
        </div>
        <button data-testid="ops-dash-refresh" onClick={load}
          className="px-3 py-1.5 border border-slate-300 rounded text-slate-600 hover:bg-slate-50 inline-flex items-center gap-1.5 text-xs font-bold">
          <RefreshCw size={13} /> Refresh
        </button>
      </div>

      {!data.commercials_visible && (
        <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-1.5" data-testid="ops-dash-masked-note">
          ₹ values are hidden — you do not hold the BOQ Commercials permission. Progress percentages remain visible.
        </div>
      )}

      {show("kpis") && (
      <div className="flex flex-wrap gap-2">
        <Kpi i={0} label="Operational Projects" value={k.ops_project_count ?? 0} sub={`${k.active_count ?? 0} active`} icon={HardHat} testid="ops-kpi-count" />
        <Kpi i={1} label="Total BOQ Value" value={inr(k.total_boq_value_ex_gst)} sub="ex-GST · approved baselines" icon={Landmark} testid="ops-kpi-boq" />
        <Kpi i={2} label="Work Value Completed" value={inr(k.executed_value_ex_gst)} sub="ex-GST · uncapped" icon={TrendingUp} testid="ops-kpi-executed" />
        <Kpi i={3} label="Overall Progress" value={k.overall_progress_pct != null ? `${k.overall_progress_pct}%` : "—"} sub="value-weighted" icon={TrendingUp} testid="ops-kpi-progress" />
        <Kpi i={4} label="Labour Deployed Today" value={k.labour_today_effective ?? 0} sub="effective count" icon={Users} testid="ops-kpi-labour" />
        <Kpi i={5} label="DPRs Today" value={k.dprs_submitted_today ?? 0} sub="submitted" icon={FileText} testid="ops-kpi-dpr" />
        <Kpi i={6} label="Pending Payment Requests" value={k.pending_payment_requests ?? 0} sub="awaiting PI" icon={Send} testid="ops-kpi-payreq" />
        <Kpi i={0} label="Within Due" value={k.within_due_count ?? 0} sub="end date not crossed" icon={CalendarClock} testid="ops-kpi-within-due" />
        <Kpi i={6} label="Overdue" value={k.overdue_count ?? 0} sub="crossed project end date" icon={CalendarX} testid="ops-kpi-overdue" />
      </div>
      )}

      {show("projects_table") && (<>
      <div className="bg-white border border-slate-200 rounded-lg px-3 py-2.5" data-testid="ops-client-counts">
        <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">
          <Users size={12} /> Projects by Client
        </div>
        <div className="flex flex-wrap gap-1.5">
          <button data-testid="ops-client-chip-all" onClick={() => setClient("")}
            className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors ${!client ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"}`}>
            All <span className="ml-1 tabular-nums">{baseProjects.length}</span>
          </button>
          {clientCounts.map(([name, count]) => (
            <button key={name} data-testid={`ops-client-chip-${name}`}
              onClick={() => setClient(client === name ? "" : name)}
              className={`px-2.5 py-1 rounded-full text-[11px] font-bold border transition-colors inline-flex items-center gap-1.5 max-w-[260px] ${client === name ? "bg-orange-600 text-white border-orange-600" : "bg-white text-slate-700 border-slate-300 hover:bg-orange-50 hover:border-orange-300"}`}
              title={name === "__none__" ? "No client set" : name}>
              <span className="truncate">{name === "__none__" ? "No client" : name}</span>
              <span className={`px-1.5 rounded-full tabular-nums text-[10px] ${client === name ? "bg-white/25" : "bg-slate-100 text-slate-700"}`}>{count}</span>
            </button>
          ))}
          {clientCounts.length === 0 && <span className="text-[11px] text-slate-400">No operational projects yet.</span>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg">
        <div className="flex items-center gap-2 flex-wrap px-3 py-2.5 border-b border-slate-200">
          <div className="text-sm font-extrabold text-slate-800 uppercase tracking-wide mr-2">Operational Projects</div>
          <div className="relative">
            <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input data-testid="ops-dash-search" className="border border-slate-300 rounded pl-7 pr-2 py-1.5 text-xs w-64"
              placeholder="Search project name / code / RO number…" value={q} onChange={(e) => setQ(e.target.value)} />
          </div>
          <select data-testid="ops-dash-client-filter" className="border border-slate-300 rounded px-2 py-1.5 text-xs"
            value={client} onChange={(e) => setClient(e.target.value)}>
            <option value="">All Clients</option>
            {clientCounts.some(([n]) => n === "__none__") && <option value="__none__">No client</option>}
            {(data.clients || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="ops-dash-type-filter" className="border border-slate-300 rounded px-2 py-1.5 text-xs"
            value={ptype} onChange={(e) => setPtype(e.target.value)}>
            <option value="">All Project Types</option>
            {workTypes.list.map((t) => <option key={t} value={t}>{t}</option>)}
            {workTypes.untyped && <option value="__none__">No type set</option>}
          </select>
          <label className="flex items-center gap-1.5 text-[11px] font-bold text-slate-600 cursor-pointer select-none"
            title="Completed projects are hidden by default">
            <input type="checkbox" data-testid="ops-dash-show-completed" checked={showCompleted}
              onChange={(e) => setShowCompleted(e.target.checked)} />
            Show completed{completedCount > 0 ? ` (${completedCount})` : ""}
          </label>
          <span className="text-[11px] text-slate-500 ml-auto" data-testid="ops-dash-row-count">{rows.length} of {baseProjects.length} projects</span>
        </div>
        <div className="max-h-[58vh] overflow-y-auto" data-testid="ops-dash-scroll">
          <table className="w-full text-xs">
            <thead className="sticky top-0 z-10">
              <tr className="text-left text-[10px] uppercase tracking-wider text-slate-600 bg-slate-100">
                <th className="px-3 py-2">Project</th>
                <th className="px-3 py-2">Client</th>
                <th className="px-3 py-2">Client POC</th>
                <th className="px-3 py-2">RO Number</th>
                <th className="px-3 py-2">End Date · Due</th>
                <th className="px-3 py-2 w-[170px]">Total Progress</th>
                <th className="px-3 py-2 text-right">Amount of Progress</th>
                <th className="px-3 py-2 text-right">BOQ Value</th>
                <th className="px-3 py-2 text-right">Labour Today</th>
                <th className="px-3 py-2">DPR Today</th>
                <th className="px-3 py-2">Status</th>
              </tr>
            </thead>
            <tbody data-testid="ops-dash-table-body">
              {rows.map((r) => (
                <tr key={r.project_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`ops-dash-row-${r.project_code}`}>
                  <td className="px-3 py-2">
                    <div className="font-mono font-bold text-[11px] text-slate-900">{r.project_code}</div>
                    <div className="max-w-[200px] truncate text-slate-600" title={r.project_name}>{r.project_name}</div>
                  </td>
                  <td className="px-3 py-2 max-w-[150px] truncate" title={r.client_name}>{r.client_name || "—"}</td>
                  <td className="px-3 py-2 max-w-[140px] truncate" title={r.client_poc}>{r.client_poc || "—"}</td>
                  <td className="px-3 py-2">
                    <span className="font-mono">{r.ro_number || "—"}</span>
                    {r.ro_status && (
                      <span className={`ml-1.5 px-1.5 py-0.5 rounded text-[9px] font-bold ${r.ro_status === "Approved" ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>
                        {r.ro_status}
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2"><DueBadge r={r} /></td>
                  <td className="px-3 py-2"><ProgressBar pct={r.progress_pct} /></td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-emerald-800">{inr(r.executed_value_ex_gst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{inr(r.total_boq_value_ex_gst)}</td>
                  <td className="px-3 py-2 text-right tabular-nums">{r.labour_today || "—"}</td>
                  <td className="px-3 py-2">
                    {r.dpr_today
                      ? <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${["Submitted", "Superseded"].includes(r.dpr_today.status) ? "bg-emerald-100 text-emerald-800" : "bg-amber-100 text-amber-800"}`}>{r.dpr_today.status === "Superseded" ? "Submitted" : r.dpr_today.status}</span>
                      : <span className="text-slate-400">—</span>}
                  </td>
                  <td className="px-3 py-2">
                    <span className={`px-1.5 py-0.5 rounded text-[9px] font-bold ${r.status === "Active" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-600"}`}>{r.status}</span>
                  </td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr><td colSpan={11} className="px-3 py-10 text-center text-slate-400" data-testid="ops-dash-empty">No operational projects match your search.</td></tr>
              )}
            </tbody>
          </table>
        </div>
        <div className="px-3 py-1.5 text-[9px] text-slate-400 border-t border-slate-100">{data.note}</div>
      </div>
      </>)}

      {show("blockers") && <OpsBlockerScopeCard />}

      {show("daywise_feed") && <OpsDaywiseFeed />}
    </div>
  );
}
